import { useEffect, useState } from 'react';
import { Plus, Send, Download, BarChart2, X, Info } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  template_id: string;
  smtp_config_id: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  opened_count: number;
  clicked_count: number;
  created_at: string;
  template?: { id: string; name: string; subject: string };
  smtp_config?: { id: string; name: string };
}

interface Template {
  id: string;
  name: string;
  subject: string;
}

interface SMTPConfig {
  id: string;
  name: string;
  from_email: string;
}

export default function BulkEmailCampaigns() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [smtpConfigs, setSmtpConfigs] = useState<SMTPConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    template_id: '',
    smtp_config_id: ''
  });

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadSMTPConfigs();
  }, []);

  const loadCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/bulk-email/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setCampaigns(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/email/templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTemplates(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    }
  };

  const loadSMTPConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/smtp/configs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSmtpConfigs(data.data.filter((c: SMTPConfig & { is_active: boolean }) => c.is_active));
      }
    } catch (error) {
      console.error('Erro ao carregar SMTP configs:', error);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const res = await fetch(`${apiUrl}/api/bulk-email/download-csv-template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-bulk-email.csv';
      a.click();
    } catch (error) {
      console.error('Erro ao baixar template:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!csvFile) {
      alert('Por favor, selecione um arquivo CSV');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('template_id', formData.template_id);
      formDataToSend.append('smtp_config_id', formData.smtp_config_id);
      formDataToSend.append('csv_file', csvFile);

      const res = await fetch(`${apiUrl}/api/bulk-email/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      const data = await res.json();
      
      if (data.success) {
        alert(`✅ ${data.message}`);
        setShowModal(false);
        loadCampaigns();
        resetForm();
      } else {
        alert(`❌ Erro: ${data.error}`);
      }
    } catch (error) {
      console.error('Erro ao criar campanha:', error);
      alert('Erro ao criar campanha');
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (!confirm('Iniciar envio da campanha agora?')) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const res = await fetch(`${apiUrl}/api/bulk-email/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (data.success) {
        alert(`✅ ${data.message}`);
        loadCampaigns();
      } else {
        alert(`❌ Erro: ${data.error}`);
      }
    } catch (error) {
      console.error('Erro ao enviar campanha:', error);
      alert('Erro ao enviar campanha');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', template_id: '', smtp_config_id: '' });
    setCsvFile(null);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      sending: 'bg-blue-100 text-blue-700 animate-pulse',
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    return <span className={`px-2 py-1 rounded text-xs ${styles[status as keyof typeof styles] || styles.draft}`}>
      {status.toUpperCase()}
    </span>;
  };

  if (loading) return <div className="p-8">Carregando...</div>;

  return (
    <div className="p-8">
      {/* Card de Instruções */}
      {showInstructions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <Info className="text-blue-600 mt-1 flex-shrink-0" size={20} />
              <div>
                <h3 className="font-bold text-blue-900 mb-2">Como Usar Variáveis nos Templates</h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p><strong>1. CSV:</strong> Cada coluna do CSV vira uma variável disponível</p>
                  <p><strong>2. Template:</strong> Use <code className="bg-blue-100 px-1 rounded">{'{{nome_coluna}}'}</code> no template de email</p>
                  <p><strong>Exemplo:</strong></p>
                  <div className="bg-white p-2 rounded border border-blue-200 mt-2">
                    <p className="font-mono text-xs mb-1">CSV: email,name,produto,preco</p>
                    <p className="font-mono text-xs mb-2">Template: Olá {'{{name}}'}, confira {'{{produto}}'} por apenas R$ {'{{preco}}'}</p>
                    <p className="font-mono text-xs text-green-700">✅ Resultado: Olá João, confira Shampoo por apenas R$ 29,90</p>
                  </div>
                  <p className="mt-2"><strong>Variáveis automáticas:</strong> <code className="bg-blue-100 px-1 rounded">{'{{email}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{name}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{phone}}'}</code></p>
                  <div className="bg-yellow-50 border border-yellow-300 rounded p-2 mt-2">
                    <p className="font-semibold text-yellow-900 mb-1">🏷️ Tags Automáticas:</p>
                    <p className="font-mono text-xs">Adicione colunas: <strong>tag_1</strong>, <strong>tag_2</strong>, <strong>tag_3</strong></p>
                    <p className="text-xs mt-1">Os contatos serão criados automaticamente com essas tags!</p>
                    <p className="font-mono text-xs text-green-700 mt-1">Exemplo: email,name,tag_1,tag_2,tag_3</p>
                  </div>
                </div>
              </div>
            </div>
            <button onClick={() => setShowInstructions(false)} className="text-blue-600 hover:text-blue-800">
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Campanhas de Email em Massa</h1>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadTemplate}
            className="bg-gray-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-600"
          >
            <Download size={20} /> Baixar CSV Modelo
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
          >
            <Plus size={20} /> Nova Campanha
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {campaigns.map(campaign => {
          const sentRate = campaign.total_recipients > 0 
            ? ((campaign.sent_count / campaign.total_recipients) * 100).toFixed(1) 
            : 0;
          const openRate = campaign.sent_count > 0 
            ? ((campaign.opened_count / campaign.sent_count) * 100).toFixed(1) 
            : 0;

          return (
            <div key={campaign.id} className="bg-white p-4 rounded shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-bold text-lg">{campaign.name}</h3>
                  <p className="text-sm text-gray-600">
                    Template: {campaign.template?.name} | SMTP: {campaign.smtp_config?.name}
                  </p>
                </div>
                {getStatusBadge(campaign.status)}
              </div>

              <div className="grid grid-cols-5 gap-4 mb-3">
                <div className="text-center">
                  <div className="text-2xl font-bold">{campaign.total_recipients}</div>
                  <div className="text-xs text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{campaign.sent_count}</div>
                  <div className="text-xs text-gray-600">Enviados ({sentRate}%)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{campaign.opened_count}</div>
                  <div className="text-xs text-gray-600">Abertos ({openRate}%)</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">{campaign.clicked_count}</div>
                  <div className="text-xs text-gray-600">Cliques</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{campaign.failed_count}</div>
                  <div className="text-xs text-gray-600">Falhas</div>
                </div>
              </div>

              <div className="flex gap-2">
                {campaign.status === 'draft' && (
                  <button
                    onClick={() => handleSendCampaign(campaign.id)}
                    className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
                  >
                    <Send size={16} /> Enviar Agora
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelectedCampaign(campaign);
                    setShowStatsModal(true);
                  }}
                  className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
                >
                  <BarChart2 size={16} /> Ver Detalhes
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Criar Campanha */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-xl">
            <h2 className="text-xl font-bold mb-4">Nova Campanha em Massa</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome da Campanha</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required
                  placeholder="Ex: Promoção Black Friday 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Template de Email</label>
                <select
                  value={formData.template_id}
                  onChange={e => setFormData({...formData, template_id: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Selecione um template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name} - {t.subject}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Servidor SMTP</label>
                <select
                  value={formData.smtp_config_id}
                  onChange={e => setFormData({...formData, smtp_config_id: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="">Selecione um servidor</option>
                  {smtpConfigs.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.from_email})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Arquivo CSV</label>
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={e => setCsvFile(e.target.files?.[0] || null)}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="bg-gray-200 px-3 py-2 rounded hover:bg-gray-300 whitespace-nowrap"
                  >
                    <Download size={16} />
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  O CSV deve conter: email, name, phone e variáveis customizadas
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Criar Campanha
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Estatísticas */}
      {showStatsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">{selectedCampaign.name}</h2>
                <p className="text-sm text-gray-600">
                  Criada em {new Date(selectedCampaign.created_at).toLocaleString('pt-BR')}
                </p>
              </div>
              <button onClick={() => setShowStatsModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="bg-gray-100 p-4 rounded text-center">
                <div className="text-3xl font-bold">{selectedCampaign.total_recipients}</div>
                <div className="text-sm">Total Destinatários</div>
              </div>
              <div className="bg-blue-100 p-4 rounded text-center">
                <div className="text-3xl font-bold text-blue-600">{selectedCampaign.sent_count}</div>
                <div className="text-sm">Enviados</div>
              </div>
              <div className="bg-green-100 p-4 rounded text-center">
                <div className="text-3xl font-bold text-green-600">{selectedCampaign.opened_count}</div>
                <div className="text-sm">Abertos</div>
              </div>
              <div className="bg-red-100 p-4 rounded text-center">
                <div className="text-3xl font-bold text-red-600">{selectedCampaign.failed_count}</div>
                <div className="text-sm">Falhas</div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowStatsModal(false)}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
